/**
 * Lender decisions and offers (docs/PLAN.md §4 PR 13, PRD J5, §6 "Lender decision",
 * AC13/AC14/AC18). A decision is the partner's answer to one submission, recorded verbatim with
 * evidence; an offer is the terms attached to a `conditional_offer` / `approved_offer` decision.
 * Both are append-only: a partner changing its mind is a *new* decision on the same submission
 * (counteroffer, expiry, withdrawal), never an edit of the old one.
 *
 * What the code refuses to do, on purpose:
 *   - derive an APR, payment or fee the partner did not state — an unstated term is `null` and
 *     is listed in `unstated_terms`, so every audience sees the gap honestly;
 *   - let one partner's decline touch another partner's submission (AC13);
 *   - accept a decision on a submission that was never sent (AC18);
 *   - present an offer to the buyer in a state with a commercial-financing disclosure law while
 *     the provider's disclosure is still `pending` (PLAN §2);
 *   - show a dealer any term at all — the dealer projection is availability only.
 *
 * Selection is the buyer's *preference* (`cases.selected_offer_id`, one per case), not a signed
 * financing agreement and not funding.
 */

import type { MoneyView } from './api-types'

export const DECISION_OUTCOMES = ['more_information', 'declined', 'conditional_offer', 'approved_offer', 'expired', 'withdrawn', 'superseded'] as const
export type DecisionOutcome = (typeof DECISION_OUTCOMES)[number]

/** Outcomes an operator may record. `superseded` is derived (a later decision on the same submission), never typed in. */
export const RECORDABLE_DECISION_OUTCOMES = ['more_information', 'declined', 'conditional_offer', 'approved_offer', 'expired', 'withdrawn'] as const
export type RecordableDecisionOutcome = (typeof RECORDABLE_DECISION_OUTCOMES)[number]

export const OFFER_OUTCOMES: readonly DecisionOutcome[] = ['conditional_offer', 'approved_offer']
export const isOfferOutcome = (o: DecisionOutcome): o is 'conditional_offer' | 'approved_offer' => OFFER_OUTCOMES.includes(o)

/** Outcomes that describe the fate of an earlier offer on the same submission and so need one to exist. */
export const OFFER_CLOSING_OUTCOMES: readonly DecisionOutcome[] = ['expired', 'withdrawn']

export const PAYMENT_FREQUENCIES = ['monthly', 'quarterly', 'semi_annual', 'annual', 'other'] as const
export type PaymentFrequency = (typeof PAYMENT_FREQUENCIES)[number]

/**
 * Whether the state-law disclosure the *provider* owes the buyer has been dealt with.
 * `pending` in a covered state keeps the offer off the buyer's screen.
 */
export const DISCLOSURE_STATUSES = ['not_required', 'pending', 'provider_delivered'] as const
export type DisclosureStatus = (typeof DISCLOSURE_STATUSES)[number]

/**
 * Where an offer stands, derived — never stored — from the decision history of its submission
 * and the calendar:
 *   `active`      the latest decision on the submission is this offer and it has not expired
 *   `expired`     `expires_at` has passed, or the partner recorded `expired`
 *   `withdrawn`   the partner recorded `withdrawn`
 *   `superseded`  a later decision on the same submission replaced it (counteroffer, decline, …)
 */
export const OFFER_STATUSES = ['active', 'expired', 'withdrawn', 'superseded'] as const
export type OfferStatus = (typeof OFFER_STATUSES)[number]

/** Names of the terms a partner may leave unstated. Listed on the view so a missing number is visibly missing. */
export const OFFER_OPTIONAL_TERMS = [
  'down_payment',
  'payment_amount',
  'rate',
  'fees',
  'residual',
  'total_finance_charge',
  'guarantee_requirements',
  'prepayment_terms',
  'buyout_terms',
  'expires_at',
] as const
export type OfferOptionalTerm = (typeof OFFER_OPTIONAL_TERMS)[number]

/** Partner-stated terms, verbatim. Every optional field is `null` when the partner did not state it. */
export interface OfferTermsView {
  /** Partner's own product name: "equipment loan", "FMV lease", "$1 buyout lease", … */
  product_type: string
  approved_amount: MoneyView
  down_payment: MoneyView | null
  term_months: number
  payment_frequency: PaymentFrequency
  payment_amount: MoneyView | null
  /** Basis points, only when the partner stated a rate as a rate. Never computed from a factor or a payment. */
  rate_bps: number | null
  rate_type: string | null
  /** The partner's rate wording verbatim ("factor 1.18", "prime + 2%"), when a number alone would mislead. */
  rate_text: string | null
  fees: MoneyView | null
  /** Buyout / residual amount, where the product has one. */
  residual: MoneyView | null
  /** Partner-stated total finance charge, if disclosed. Tex does not compute it. */
  total_finance_charge: MoneyView | null
  guarantee_requirements: string[]
  prepayment_terms: string | null
  buyout_terms: string | null
  conditions: string[]
  /** ISO date; `null` when the partner gave no expiry. */
  expires_at: string | null
  /** Terms the partner did not state; the UI shows these as "not stated", never as 0 or a guess. */
  unstated_terms: OfferOptionalTerm[]
}

export interface OfferDisclosureView {
  status: DisclosureStatus
  document_id: string | null
  /** Whether the buyer's state (or the equipment-use state) has a disclosure law that applies at this amount. */
  covered_state: string | null
  /** False while `covered_state` is set and `status` is `pending`: the offer stays off the buyer's screen. */
  presentable_to_buyer: boolean
  recorded_at: string
}

export interface OfferView {
  id: string
  case_id: string
  decision_id: string
  submission_id: string
  partner_id: string
  partner_display_name: string
  outcome: 'conditional_offer' | 'approved_offer'
  status: OfferStatus
  /** The buyer's current preference points at this offer. Not a contract, not funding. */
  selected: boolean
  terms: OfferTermsView
  disclosure: OfferDisclosureView
  terms_document_id: string | null
  synthetic: boolean
  recorded_at: string
}

/** Tex-only: the partner's answer as recorded, including declines and their reasons. */
export interface LenderDecisionView {
  id: string
  submission_id: string
  case_id: string
  partner_id: string
  partner_display_name: string
  outcome: DecisionOutcome
  /** Partner's own wording, verbatim. Private to Tex; the formal notice is the partner's. */
  partner_reason_text: string | null
  conditions: string[]
  requested_information: string[]
  evidence_document_id: string | null
  external_reference: string | null
  /** Adapter-supplied event id, when the decision arrived through one; the dedupe key for replays. */
  provider_event_id: string | null
  /** Partner-stated decision time; `recorded_at` is when Tex entered it. */
  decided_at: string | null
  recorded_by_user_id: string | null
  recorded_at: string
  synthetic: boolean
  /** Set for offer-bearing outcomes. */
  offer_id: string | null
  /** True while this is the latest decision on its submission. */
  current: boolean
}

/**
 * The one line every audience may see about offers (PRD J5, AC13). The dealer gets exactly this
 * and nothing else; the buyer and Tex get it alongside the offers themselves.
 *   `not_yet_submitted`     nothing has been put in front of a partner
 *   `awaiting_partner`      at least one submission is with a partner and undecided, no offer yet
 *   `awaiting_disclosure`   an offer exists but cannot be shown to the buyer until the provider's disclosure is dealt with
 *   `offer_available`       at least one active, presentable offer
 *   `offer_selected`        the buyer has chosen one
 *   `no_offer_available`    every partner has answered and none holds an open offer
 */
export const OFFER_AVAILABILITIES = ['not_yet_submitted', 'awaiting_partner', 'awaiting_disclosure', 'offer_available', 'offer_selected', 'no_offer_available'] as const
export type OfferAvailability = (typeof OFFER_AVAILABILITIES)[number]

export const OFFER_AVAILABILITY_MESSAGES: Record<OfferAvailability, string> = {
  not_yet_submitted: 'Not yet with a funding partner.',
  awaiting_partner: 'With a funding partner; awaiting their response.',
  awaiting_disclosure: 'An offer is being finalized; Tex will follow up.',
  offer_available: 'An offer is available for the buyer to review.',
  offer_selected: 'The buyer has selected an offer.',
  no_offer_available: 'No offer currently available; Tex will follow up.',
}

export interface OfferAvailabilityView {
  availability: OfferAvailability
  message: string
  /** Counts only, so a dealer can see progress without seeing terms. */
  submissions_awaiting: number
  offers_active: number
}

/** `GET /cases/:id/offers` for a dealer: availability, nothing else (AC13 privacy). */
export interface DealerOffersResponse {
  audience: 'dealer'
  availability: OfferAvailabilityView
}

/** `GET /cases/:id/offers` for the buyer: only offers presentable to them, no decline reasons. */
export interface BorrowerOffersResponse {
  audience: 'borrower'
  availability: OfferAvailabilityView
  offers: OfferView[]
  selected_offer_id: string | null
  /** The case's optimistic-concurrency token, needed to select. */
  case_version: number
}

/** `GET /cases/:id/offers` for Tex: everything, including offers currently withheld from the buyer. */
export interface TexOffersResponse {
  audience: 'tex'
  availability: OfferAvailabilityView
  offers: OfferView[]
  selected_offer_id: string | null
  case_version: number
}

export type OffersResponse = DealerOffersResponse | BorrowerOffersResponse | TexOffersResponse

export interface LenderDecisionListResponse {
  decisions: LenderDecisionView[]
}

export interface MoneyInput {
  amount_minor: number
  currency: 'USD'
}

/** Terms as the operator transcribes them from the partner's letter. Omit what the partner did not state. */
export interface OfferTermsInput {
  product_type: string
  approved_amount: MoneyInput
  down_payment?: MoneyInput
  term_months: number
  payment_frequency: PaymentFrequency
  payment_amount?: MoneyInput
  rate_bps?: number
  rate_type?: string
  rate_text?: string
  fees?: MoneyInput
  residual?: MoneyInput
  total_finance_charge?: MoneyInput
  guarantee_requirements?: string[]
  prepayment_terms?: string
  buyout_terms?: string
  conditions?: string[]
  expires_at?: string
  terms_document_id?: string
  disclosure: DisclosureInput
}

/**
 * `not_required` needs the operator's reason; `provider_delivered` needs the disclosure
 * document itself; `pending` needs nothing and withholds the offer in covered states.
 */
export type DisclosureInput =
  | { status: 'pending' }
  | { status: 'not_required'; note: string }
  | { status: 'provider_delivered'; document_id: string }

/** `POST /cases/:id/submissions/:submissionId/decisions` (Tex; `Idempotency-Key` required). */
export interface RecordDecisionBody {
  outcome: RecordableDecisionOutcome
  partner_reason_text?: string
  conditions?: string[]
  requested_information?: string[]
  /** The partner's letter / portal capture. Required for a live partner. */
  evidence_document_id?: string
  external_reference?: string
  /** Partner-stated decision time. */
  decided_at?: string
  /** Required for `conditional_offer` / `approved_offer`; refused otherwise. */
  offer?: OfferTermsInput
}

/** Response to either decision-recording route. `offer` is set for offer-bearing outcomes. */
export interface RecordDecisionResponse {
  decision: LenderDecisionView
  offer: OfferView | null
}

/** `POST /cases/:id/submissions/:submissionId/decisions/adapter` — a provider event, normalized by the submission's adapter (mock only in P0). */
export interface RecordAdapterDecisionBody {
  /** The adapter's own id for this event; a replay with the same id is a no-op that returns the existing decision. */
  provider_event_id: string
  raw: unknown
  /** Offer terms are never normalized from a raw event; the operator supplies them for offer-bearing outcomes. */
  offer?: OfferTermsInput
}

/** `POST /cases/:id/offers/:offerId/disclosure` (Tex): later word on the provider's disclosure; append-only. */
export interface RecordOfferDisclosureBody {
  disclosure: DisclosureInput
}

/** `POST /cases/:id/offer-selection` (buyer representative, or Tex on the buyer's instruction). `offer_id: null` clears the preference. */
export interface SelectOfferBody {
  expected_version: number
  offer_id: string | null
  /** Required when Tex records the selection: how the buyer's instruction reached Tex. */
  note?: string
}

export interface OfferSelectionResponse {
  case_id: string
  selected_offer_id: string | null
  case_version: number
  availability: OfferAvailabilityView
}
